import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import type { SpendChipCategory } from './spendChip';

export type { SpendChipCategory };

type Props = {
  categories: SpendChipCategory[];
  selectedId: string;
  onSelect: (id: string) => void;
};

function Chip({
  label,
  selected,
  color,
  onPress,
}: {
  label: string;
  selected: boolean;
  color: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={({ pressed }) => [
        styles.chip,
        selected && { borderColor: color, backgroundColor: `${color}28` },
        pressed && styles.pressed,
      ]}
    >
      <View style={[styles.chipDot, { backgroundColor: color }]} />
      <Text style={[styles.chipLabel, selected && styles.chipLabelSelected]} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

/** Web chip rail with CSS edge fades so overflow reads clearly. */
export function SpendCategoryChipRail({ categories, selectedId, onSelect }: Props) {
  return (
    <View style={styles.rail}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabs}
      >
        <Chip
          label="All"
          selected={selectedId === 'all'}
          color={colors.accent}
          onPress={() => onSelect('all')}
        />
        {categories.map((cat) => (
          <Chip
            key={cat.id}
            label={cat.name}
            selected={selectedId === cat.id}
            color={cat.color}
            onPress={() => onSelect(cat.id)}
          />
        ))}
      </ScrollView>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 16,
          pointerEvents: 'none',
          background:
            'linear-gradient(90deg, rgba(21,27,36,0.95) 0%, rgba(21,27,36,0) 100%)',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 40,
          pointerEvents: 'none',
          background:
            'linear-gradient(270deg, rgba(21,27,36,0.98) 10%, rgba(21,27,36,0) 100%)',
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  rail: {
    position: 'relative',
  },
  tabs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 4,
    paddingRight: spacing.xl,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 34,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  chipDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  chipLabel: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
    fontSize: 12,
    maxWidth: 110,
  },
  chipLabelSelected: {
    color: colors.text,
  },
  pressed: {
    opacity: 0.85,
  },
});
